import React from 'react';
import { FaChartLine, FaCalendarAlt, FaTrophy, FaUsers, FaShieldAlt, FaRocket, FaTarget } from 'react-icons/fa';
import KPICard from './KPICard';
import QuickActions from './QuickActions';
import ActivityFeed from './ActivityFeed';
import BarChart from './BarChart';
import LineChart from './Linechart';
import Spinner from '../common/Spinner';

const EnhancedDashboard = React.forwardRef(({ 
  metrics, 
  profitS, 
  labels, 
  datas,
  cumulativeDatas,
  isLinechart, 
  loadingMetrics = false,
  loadingPNL = false,
  loadingStrategies = false,
  loadingActivities = false,
  activities = [],
  onActionClick 
}, ref) => {
  const [chartType, setChartType] = React.useState('daily'); // 'daily' for bar chart, 'cumulative' for line chart
  // Calculate change indicators
  const calculateChange = (current, previous, isPercentage = false) => {
    // Only return null if previous is truly missing, not if it's 0 (which is a valid value)
    if (previous === null || previous === undefined || previous === '') return null;
    const currentVal = parseFloat(current || 0);
    const previousVal = parseFloat(previous || 0);
    
    // If both are 0, no meaningful change to show
    if (currentVal === 0 && previousVal === 0) return null;
    
    const change = currentVal - previousVal;
    
    if (isPercentage) {
      // For percentage changes, show percentage point difference
      const changePercent = change.toFixed(1);
      return {
        value: change > 0 ? `+${changePercent}%` : `${changePercent}%`,
        type: change >= 0 ? 'positive' : 'negative'
      };
    } else {
      // For absolute values, show absolute change and percentage if previous is not zero
      const changePercent = previousVal !== 0 ? ((change / Math.abs(previousVal)) * 100).toFixed(1) : null;
      return {
        value: change > 0 ? `+${change}` : `${change}`,
        percent: changePercent ? (parseFloat(changePercent) > 0 ? `+${changePercent}%` : `${changePercent}%`) : null,
        type: change >= 0 ? 'positive' : 'negative'
      };
    }
  };

  // Calculate risk score based on loss percentage
  // Risk score: 0-100 scale where 0 is lowest risk (no losing days), 100 is highest risk (all losing days)
  const calculateRiskScore = (lossPct) => {
    if (lossPct === null || lossPct === undefined || lossPct === '') return null;
    // Loss percentage directly represents risk: 0% loss = 0 risk, 100% loss = 100 risk
    return Math.round(parseFloat(lossPct));
  };

  // Calculate all change indicators
  const totalProfitChange = calculateChange(
    metrics?.overall_pnl || 0,
    metrics?.prev_overall_pnl || 0
  );

  const activeStrategiesChange = calculateChange(
    metrics?.active_strategy_cnt || 0,
    metrics?.prev_active_strategy_cnt || 0
  );

  const winRateChange = calculateChange(
    metrics?.win_pct || 0,
    metrics?.prev_win_pct || 0,
    true // isPercentage = true for win rate
  );

  const tradingDaysChange = calculateChange(
    parseInt(metrics?.no_of_days_traded || 0),
    parseInt(metrics?.prev_no_of_days_traded || 0)
  );

  const avgPnlChange = calculateChange(
    metrics?.avg_pnl || 0,
    metrics?.prev_avg_pnl || 0
  );

  const riskScore = calculateRiskScore(metrics?.loss_pct);
  
  // Calculate risk score change (comparing loss percentages)
  // For risk score, lower is better, so we invert the change type
  const prevLossPct = metrics?.prev_loss_pct || 0;
  const prevRiskScore = prevLossPct ? Math.round(parseFloat(prevLossPct)) : null;
  const riskScoreChange = prevRiskScore !== null && riskScore !== null ? (() => {
    const change = riskScore - prevRiskScore;
    // For risk: negative change (decrease) is positive, positive change (increase) is negative
    return {
      value: change > 0 ? `+${change}` : `${change}`,
      type: change <= 0 ? 'positive' : 'negative' // Inverted: decrease in risk is positive
    };
  })() : null;

  // Enhanced KPI data with additional metrics
  const enhancedKPIs = [
    {
      title: 'Total Profit',
      value: metrics?.overall_pnl || 0,
      change: totalProfitChange ? (totalProfitChange.percent || totalProfitChange.value) : null,
      changeType: totalProfitChange ? totalProfitChange.type : 'neutral',
      icon: <span style={{ fontSize: '20px', fontWeight: 'bold' }}>₹</span>,
      color: '#4DD5B8',
      subtitle: 'vs last month',
      formatValue: (val) => `₹${val}`
    },
    {
      title: 'Active Strategies',
      value: metrics?.active_strategy_cnt || 0,
      change: activeStrategiesChange ? activeStrategiesChange.value : null,
      changeType: activeStrategiesChange ? activeStrategiesChange.type : 'neutral',
      icon: <FaRocket />,
      color: '#FF823C',
      subtitle: 'running strategies'
    },
    {
      title: 'Win Rate',
      value: metrics?.win_pct !== undefined && metrics?.win_pct !== null ? parseFloat(metrics.win_pct) : null,
      change: winRateChange ? winRateChange.value : null,
      changeType: winRateChange ? winRateChange.type : 'neutral',
      icon: <FaTrophy />,
      color: '#FFC833',
      subtitle: 'success rate',
      formatValue: (val) => val !== null && val !== undefined ? `${val}%` : 'Not Available'
    },
    {
      title: 'Trading Days',
      value: metrics?.no_of_days_traded || 0,
      change: tradingDaysChange ? tradingDaysChange.value : null,
      changeType: tradingDaysChange ? tradingDaysChange.type : 'neutral',
      icon: <FaCalendarAlt />,
      color: '#17a2b8',
      subtitle: 'this month'
    },
    {
      title: 'Avg P&L per Day',
      value: metrics?.avg_pnl ? parseFloat(metrics.avg_pnl) : null,
      change: avgPnlChange ? avgPnlChange.value : null,
      changeType: avgPnlChange ? avgPnlChange.type : 'neutral',
      icon: <FaChartLine />,
      color: '#28a745',
      subtitle: 'average daily profit',
      formatValue: (val) => val !== null && val !== undefined ? `${val.toFixed(2)}` : 'Not Available'
    },
    {
      title: 'Risk Score',
      value: riskScore,
      change: riskScoreChange && riskScore !== null ? riskScoreChange.value : null,
      changeType: riskScore !== null ? (riskScore < 30 ? 'positive' : riskScore < 70 ? 'neutral' : 'negative') : 'neutral',
      icon: <FaShieldAlt />,
      color: '#6f42c1',
      subtitle: riskScore !== null ? (riskScore < 30 ? 'low risk' : riskScore < 70 ? 'moderate risk' : 'high risk') : 'Not Available',
      formatValue: (val) => val !== null && val !== undefined ? val : 'Not Available'
    }
  ];

  return (
    <div ref={ref} className="enhanced-dashboard" style={{
      padding: '12px',
      background: '#f8f9fa',
      minHeight: '100vh'
    }}>
      {/* Header Section */}
      <div className="dashboard-header mb-2">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1 className="mb-0 fw-bold" style={{ color: '#151B22', fontSize: '20px', lineHeight: '1.2' }}>
              Dashboard Overview
            </h1>
            <p className="mb-0 mt-1" style={{ color: '#495057', fontSize: '12px' }}>
              Welcome back! Here's what's happening with your trading strategies.
            </p>
          </div>
          <div className="d-flex align-items-center gap-2">
            <div style={{
              padding: '4px 10px',
              background: '#ffffff',
              borderRadius: '6px',
              border: '1px solid #e9ecef',
              fontSize: '11px',
              color: '#495057'
            }}>
              Last updated: {new Date().toLocaleTimeString()}
            </div>
          </div>
        </div>
      </div>

      {/* Snapshot Content - Metrics and Chart */}
      <div className="snapshot-content" style={{ background: '#f8f9fa' }}>
        {/* KPI Cards Section */}
        <div className="kpi-section mb-2">
          <div className="row g-2">
            {enhancedKPIs.map((kpi, index) => (
              <div key={index} className="col-lg-4 col-md-6">
                <KPICard {...kpi} loading={loadingMetrics} />
              </div>
            ))}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="row g-2">
          {/* Left Column - Chart */}
          <div className="col-lg-8">
            {/* Chart Section */}
            <div className="chart-section mb-2">
              <div style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                borderRadius: '10px',
                padding: '12px',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.06)',
                border: '1px solid #e9ecef',
                height: '280px',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h4 className="mb-0 fw-bold" style={{ color: '#151B22', fontSize: '16px' }}>
                    Performance Overview
                  </h4>
                  <div className="d-flex align-items-center gap-2">
                    <div className="d-flex align-items-center gap-1">
                      <span style={{ fontSize: '11px', color: '#495057', fontWeight: chartType === 'daily' ? '600' : '400' }}>Daily</span>
                      <label className="switch" style={{ margin: 0 }}>
                        <input 
                          type="checkbox" 
                          checked={chartType === 'cumulative'}
                          onChange={(e) => setChartType(e.target.checked ? 'cumulative' : 'daily')}
                        />
                        <span className="slider round"></span>
                      </label>
                      <span style={{ fontSize: '11px', color: '#495057', fontWeight: chartType === 'cumulative' ? '600' : '400' }}>Cumulative</span>
                    </div>
                    <span style={{ fontSize: '11px', color: '#495057' }}>Last 30 days</span>
                  </div>
                </div>

                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  {loadingPNL ? (
                    <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <Spinner size="md" text="Loading chart data..." />
                    </div>
                  ) : (datas.length > 0 && labels.length > 0) ? (
                    <div style={{ flex: 1, width: '100%', overflow: 'hidden', position: 'relative', minHeight: 0 }}>
                      {chartType === 'cumulative' ? (
                        <LineChart datas={cumulativeDatas && cumulativeDatas.length > 0 ? cumulativeDatas : datas} labels={labels} />
                      ) : (
                        <BarChart datas={datas} labels={labels} />
                      )}
                    </div>
                  ) : (
                    <div className="d-flex justify-content-center align-items-center" style={{ flex: 1 }}>
                      <div className="text-center">
                        <FaChartLine style={{ fontSize: '48px', color: '#6c757d', marginBottom: '16px' }} />
                        <p style={{ color: '#495057', margin: 0, fontSize: '15px' }}>No chart data available</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Activity Feed and Top Strategies */}
          <div className="col-lg-4">
            {/* Activity Feed */}
            <div className="activity-section mb-2" style={{ height: '280px' }}>
              <ActivityFeed activities={activities} loading={loadingActivities} />
            </div>

          {/* Top Strategies Section */}
          {loadingStrategies ? (
            <div className="top-strategies-section">
              <div style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                borderRadius: '10px',
                padding: '12px',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.06)',
                border: '1px solid #e9ecef'
              }}>
                <div className="d-flex align-items-center mb-2">
                  <div style={{
                    height: '16px',
                    width: '120px',
                    background: '#e9ecef',
                    borderRadius: '4px',
                    animation: 'pulse 1.5s ease-in-out infinite'
                  }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {[1, 2, 3].map((i) => (
                    <div key={i} style={{
                      padding: '10px',
                      background: '#ffffff',
                      borderRadius: '8px',
                      border: '1px solid #e9ecef'
                    }}>
                      <div style={{
                        height: '12px',
                        width: '60%',
                        background: '#e9ecef',
                        borderRadius: '4px',
                        marginBottom: '6px',
                        animation: 'pulse 1.5s ease-in-out infinite'
                      }} />
                      <div style={{
                        height: '10px',
                        width: '40%',
                        background: '#e9ecef',
                        borderRadius: '4px',
                        animation: 'pulse 1.5s ease-in-out infinite'
                      }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : profitS?.length > 0 && (
            <div className="top-strategies-section">
              <div style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                borderRadius: '10px',
                padding: '12px',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.06)',
                border: '1px solid #e9ecef'
              }}>
                <div className="d-flex align-items-center mb-2">
                  <h4 className="mb-0 fw-bold" style={{ color: '#151B22', fontSize: '16px' }}>
                    Top Strategies
                  </h4>
                  <div style={{
                    width: '30px',
                    height: '2px',
                    background: 'linear-gradient(90deg, #4DD5B8 0%, #FF823C 100%)',
                    borderRadius: '2px',
                    marginLeft: '8px'
                  }} />
                </div>

                <div className="strategies-list" style={{
                  maxHeight: '240px',
                  overflowY: 'auto'
                }}>
                  {profitS.slice(0, 3).map((strategy, index) => (
                    <div 
                      key={index}
                      style={{
                        padding: '10px',
                        marginBottom: '8px',
                        background: '#ffffff',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <h6 className="mb-0 fw-semibold" style={{ 
                          color: '#151B22', 
                          fontSize: '12px',
                          margin: 0
                        }}>
                          {strategy.strategy_name}
                        </h6>
                        <span style={{
                          fontSize: '10px',
                          padding: '2px 6px',
                          background: strategy.is_subscribed === 'Y' ? '#d4edda' : '#fff3cd',
                          color: strategy.is_subscribed === 'Y' ? '#155724' : '#856404',
                          borderRadius: '6px',
                          fontWeight: '500'
                        }}>
                          {strategy.is_subscribed === 'Y' ? 'Active' : 'Available'}
                        </span>
                      </div>
                      
                      <div className="mb-1">
                        {strategy.tags.slice(0, 2).map((tag, tagIndex) => (
                          <span 
                            key={tagIndex}
                            style={{
                              fontSize: '10px',
                              padding: '2px 4px',
                              background: '#f8f9fa',
                              color: '#495057',
                              borderRadius: '6px',
                              marginRight: '4px',
                              fontWeight: '500'
                            }}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      
                      <p style={{ 
                        color: '#495057', 
                        fontSize: '10px',
                        margin: 0,
                        lineHeight: '1.3',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {strategy.strategy_desc.replace(/<[^>]*>/g, '')}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* End of row with Chart and Activity Feed */}

        {/* Quick Actions Section */}
        <div className="quick-actions-section mb-2">
          <QuickActions onActionClick={onActionClick} />
        </div>
      </div>
      {/* End Snapshot Content */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .switch {
          position: relative;
          display: inline-block;
          width: 44px;
          height: 24px;
        }
        .switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .switch .slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          transition: .4s;
          border-radius: 24px;
        }
        .switch .slider:before {
          position: absolute;
          content: "";
          height: 18px;
          width: 18px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: .4s;
          border-radius: 50%;
        }
        .switch input:checked + .slider {
          background-color: #4DD5B8;
        }
        .switch input:checked + .slider:before {
          transform: translateX(20px);
        }
        .switch input:focus + .slider {
          box-shadow: 0 0 1px #4DD5B8;
        }
      `}</style>
    </div>
  );
});

EnhancedDashboard.displayName = 'EnhancedDashboard';

export default EnhancedDashboard;
